import { PageHeader, Stat, Stats } from '../../components/office';
import { useEffect, useMemo, useState } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, AlertCircle } from 'lucide-react';
import { auditApi } from '@verlo/shared/services/api';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';

// Every event `audit_log.AuditEvent` writes, in the words the restaurant uses. A key missing
// here shows as itself, which is how `promotion_changed` reached a restaurant's screen.
const EVENT_LABELS = {
  login_success:          'Signed in',
  login_failure:          'Sign-in failed',
  logout:                 'Signed out',
  sessions_revoked:       'Signed out everywhere',
  session_reuse_detected: 'Sign-in reused',
  recovery_requested:     'Password reset asked for',
  password_set:           'Password set',
  mfa_enabled:            'Two-factor on',
  mfa_disabled:           'Two-factor off',
  mfa_reset:              'Two-factor reset',
  user_invited:           'Account invited',
  role_changed:           'Access changed',
  permission_denied:      'Not allowed',
  landing_changed:        'Landing page changed',
  pin_set:                'PIN set',
  pin_reset:              'PIN reset',
  pin_login_success:      'Till PIN used',
  pin_login_failure:      'Wrong till PIN',
  pin_lockout:            'Locked out of the till',
  pin_sheet_viewed:       'PIN list opened',
  manager_override:       'Manager override',
  order_refunded:         'Refund',
  order_cancelled:        'Order cancelled',
  item_voided:            'Item voided',
  discount_applied:       'Discount',
  discount_removed:       'Discount taken off',
  item_comped:            'Item on the house',
  item_uncomped:          'On the house undone',
  price_overridden:       'Price changed on a bill',
  no_sale:                'Drawer opened, no sale',
  typed_extra:            'Typed extra',
  line_amended:           'Line changed',
  order_message:          'Note to the kitchen',
  order_message_removed:  'Kitchen note removed',
  till_opened:            'Till opened',
  till_closed:            'Till closed',
  till_settings:          'Cash settings changed',
  cash_movement:          'Cash in or out',
  safe_movement:          'Safe',
  promotion_changed:      'Offer changed',
  promotion_applied:      'Offer applied',
  promotion_removed:      'Offer taken off',
  menu_promoted:          'Menu published',
  menu_unpromoted:        'Menu unpublished',
  menu_section_renamed:   'Menu section renamed',
  ingredient_recosted:    'Ingredient price · menu re-costed',
  sub_recipe_recosted:    'Sub-recipe price · menu re-costed',
  insight_generated:      'Insight written',
  printer_changed:        'Printer changed',
  print_reprinted:        'Reprinted',
  print_receipt:          'Receipt printed',
  print_retried:          'Print retried',
  print_dismissed:        'Print dismissed',
  node_registered:        'Node registered',
  node_enrolled:          'Node enrolled',
  node_enroll_rejected:   'Node enrolment refused',
  node_revoked:           'Node cut off',
  node_token_reissued:    'Node key reissued',
  node_registry_issued:   'Node registry issued',
  node_audit_rejected:    'Node record refused',
  sync_row_rejected:      'Sync refused a change',
  sync_dead_letter:       'Sync gave up on a change',
};

// "Refund £16.50 on order 4fc2b2e9-66e8-4152-9d38-38074d01d479 · Quality complaint": the id
// is for a machine, and pushes the reason out of sight. Its first eight characters say which
// order; hovering the cell shows the whole line.
const shortIds = (text) => text && text.replace(/\b([0-9a-f]{8})-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi, '$1');

const SEVERITY_STYLES = {
  info:     { dot: 'bg-ink-faint',  pill: 'bg-surface-2 text-ink-soft border-hairline',     icon: CheckCircle2 },
  warn:     { dot: 'bg-signal-warn-line',  pill: 'bg-signal-warn-tint text-signal-warn border-signal-warn-tint',     icon: AlertCircle },
  critical: { dot: 'bg-signal-stop-line',   pill: 'bg-signal-stop-tint  text-signal-stop  border-signal-stop-line',      icon: AlertTriangle },
};

const SINCE_OPTIONS = [
  { value: '1',    label: 'Last hour' },
  { value: '24',   label: 'Last 24 hours' },
  { value: '168',  label: 'Last 7 days' },
  { value: '720',  label: 'Last 30 days' },
];

const SEVERITY_OPTIONS = [
  { value: 'all',      label: 'All severities' },
  { value: 'critical', label: 'Critical only' },
  { value: 'warn',     label: 'Warnings + critical' },
  { value: 'info',     label: 'Info only' },
];

const EVENT_FILTERS = [
  { value: 'all', label: 'Everything' },
  { value: 'login_success,login_failure,logout,sessions_revoked,session_reuse_detected,recovery_requested,password_set,mfa_enabled,mfa_disabled,mfa_reset', label: 'Signing in' },
  { value: 'pin_login_success,pin_login_failure,pin_lockout,pin_set,pin_reset,pin_sheet_viewed', label: 'Till PINs' },
  { value: 'order_refunded,order_cancelled,item_voided,discount_applied,discount_removed,item_comped,item_uncomped,price_overridden,no_sale,manager_override', label: 'Refunds, voids and discounts' },
  { value: 'till_opened,till_closed,till_settings,cash_movement,safe_movement,typed_extra', label: 'Cash' },
  { value: 'user_invited,role_changed,permission_denied,landing_changed', label: 'Accounts and access' },
  { value: 'promotion_changed,promotion_applied,promotion_removed,menu_promoted,menu_unpromoted,menu_section_renamed,ingredient_recosted,sub_recipe_recosted', label: 'Menu and offers' },
];

const AuditLogPage = () => {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [since, setSince] = useState('24');
  const [severity, setSeverity] = useState('all');
  const [eventType, setEventType] = useState('all');
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = { since_hours: parseInt(since, 10), limit: 200 };
      if (severity !== 'all') params.severity = severity;
      if (eventType !== 'all') params.event_type = eventType;
      const [l, s] = await Promise.all([
        auditApi.list(params),
        auditApi.summary(parseInt(since, 10)),
      ]);
      setRows(l.data.rows || []);
      setSummary(s.data.by_type || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [since, severity, eventType]);

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    // Anything shown in the row: the people, the event (its words or its key), the till, the detail.
    return rows.filter((r) => [
      r.user_id_name, r.actor_id_name, r.target_user_id_name, r.actor_email,
      r.detail, r.event_type, EVENT_LABELS[r.event_type], r.device_id, r.ip,
    ].some((v) => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const totals = useMemo(() => {
    const t = { info: 0, warn: 0, critical: 0 };
    summary.forEach((s) => { t[s.severity] = (t[s.severity] || 0) + s.count; });
    return t;
  }, [summary]);

  return (
    <div data-testid="audit-log-page">
      <div>
        <PageHeader
          title="Audit log"
          purpose="Every sign-in, PIN, refund, void, discount and override: who, when and on which till. Nothing here can be changed or deleted."
          actions={(
            <Button variant="outline" size="sm" className="gap-1.5" onClick={load} data-testid="audit-refresh">
              <RefreshCw size={13} />
              Refresh
            </Button>
          )}
        />

        {/* Severity tiles — tap one to filter */}
        <Stats className="mb-6" data-testid="audit-tiles">
          {[['info', 'Info', 'info'], ['warn', 'Warnings', 'warn'], ['critical', 'Critical', 'stop']].map(([sev, label, tone]) => (
            <Stat
              key={sev}
              label={label}
              value={totals[sev] || 0}
              hint={`events · ${SINCE_OPTIONS.find((s) => s.value === since)?.label.toLowerCase()}`}
              delta={severity === sev ? { text: 'Filtering', tone } : undefined}
              onClick={() => setSeverity(severity === sev ? 'all' : sev)}
              className={severity === sev ? 'border-verlo ring-1 ring-inset ring-verlo' : ''}
              testId={`audit-tile-${sev}`}
            />
          ))}
        </Stats>

        {/* Filters */}
        <div className="bg-white border border-hairline rounded-lg p-4 mb-4 flex items-center gap-3 flex-wrap" data-testid="audit-filters">
          <Input
            placeholder="Search a name, a till, an event…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-xs h-9 text-[12px]"
            data-testid="audit-search"
          />
          <Select value={since} onValueChange={setSince}>
            <SelectTrigger className="h-9 w-[160px] text-[12px]" data-testid="audit-since"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SINCE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={severity} onValueChange={setSeverity}>
            <SelectTrigger className="h-9 w-[180px] text-[12px]" data-testid="audit-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SEVERITY_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger className="h-9 w-[220px] text-[12px]" data-testid="audit-event-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {EVENT_FILTERS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="bg-white border border-hairline rounded-lg overflow-hidden" data-testid="audit-table">
          <div className="overflow-x-auto">
            <table className="w-full text-[12px]">
              <thead className="bg-surface-2">
                <tr>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">When</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">Event</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">Who</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">By · to</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">Where</th>
                  <th className="text-left px-4 py-2.5 font-semibold text-ink-soft">Detail</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-ink-faint">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-ink-faint" data-testid="audit-empty">
                    No events match the current filters.
                  </td></tr>
                )}
                {!loading && filtered.map((r) => {
                  const styles = SEVERITY_STYLES[r.severity] || SEVERITY_STYLES.info;
                  const Icon = styles.icon;
                  return (
                    <tr key={r.id} className="border-t border-hairline" data-testid={`audit-row-${r.id}`}>
                      <td className="px-4 py-3 text-ink-soft font-mono-display whitespace-nowrap">
                        {new Date(r.event_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'medium' })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap text-[11px] font-semibold px-2 py-0.5 rounded-md border ${styles.pill}`}>
                          <Icon size={11} />
                          {EVENT_LABELS[r.event_type] || r.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-ink-soft">{r.user_id_name || (r.user_id ? <code className="text-[11px] text-ink-faint">{r.user_id.slice(0, 8)}</code> : '—')}</td>
                      <td className="px-4 py-3 text-ink-soft">
                        {/* Verlo's own staff belong to no restaurant, so they have no name here: their email instead. */}
                        {(r.actor_id_name || r.actor_email) && <span className="font-semibold">{r.actor_id_name || r.actor_email}</span>}
                        {(r.actor_id_name || r.actor_email) && r.target_user_id_name && <span className="text-ink-faint mx-1">→</span>}
                        {r.target_user_id_name && <span>{r.target_user_id_name}</span>}
                        {!r.actor_id_name && !r.actor_email && !r.target_user_id_name && '—'}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-ink-soft font-mono-display">
                        {r.device_id && <span title="device" className="whitespace-nowrap">{r.device_id.slice(0, 18)}</span>}
                        {r.ip && <span className={`text-ink-faint whitespace-nowrap ${r.device_id ? 'ml-2' : ''}`}>{r.ip}</span>}
                        {!r.device_id && !r.ip && '—'}
                      </td>
                      <td className="px-4 py-3 text-ink-soft max-w-md truncate" title={r.detail || undefined}>{shortIds(r.detail) || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-[11px] text-ink-faint mt-4 text-center">
          Showing {filtered.length} of {rows.length} events.
        </p>
      </div>
    </div>
  );
};

export default AuditLogPage;
